import React from 'react';
import { 
  BarChart3, 
  FileSearch, 
  Edit, 
  User, 
  Bell, 
  Settings,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { classNames } from '../../utils/classNames';

export type WorkflowType = 'policy-analysis' | 'bill-tracking' | 'redlines' | 'legislator-profiles' | 'alerts' | 'settings';

interface SidebarProps {
  activeWorkflow: WorkflowType;
  onWorkflowChange: (workflow: WorkflowType) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const workflows = [
  {
    id: 'policy-analysis' as WorkflowType,
    name: 'Policy Analysis',
    icon: BarChart3,
    description: 'AI-powered legislative intelligence',
    available: true,
  },
  {
    id: 'bill-tracking' as WorkflowType,
    name: 'Bill Tracking',
    icon: FileSearch,
    description: 'Monitor legislation progress',
    available: false, // Coming soon
  },
  {
    id: 'redlines' as WorkflowType,
    name: 'Redlines',
    icon: Edit,
    description: 'Document comparison & analysis',
    available: false, // Coming soon
  },
  {
    id: 'legislator-profiles' as WorkflowType,
    name: 'Legislator Profiles',
    icon: User,
    description: 'Member analysis & voting records',
    available: false, // Coming soon
  },
  {
    id: 'alerts' as WorkflowType,
    name: 'Alerts',
    icon: Bell,
    description: 'Custom notifications',
    available: false, // Coming soon
  },
  {
    id: 'settings' as WorkflowType,
    name: 'Settings',
    icon: Settings,
    description: 'App configuration',
    available: true, // Now available!
  },
];

export const Sidebar: React.FC<SidebarProps> = ({
  activeWorkflow,
  onWorkflowChange,
  isCollapsed,
  onToggleCollapse,
}) => {
  return (
    <div className={classNames(
      'theme-bg-sidebar backdrop-blur-sm border-r theme-border-sidebar transition-all duration-300 flex flex-col h-full',
      isCollapsed ? 'w-16' : 'w-64'
    )}>
      {/* Header */}
      <div className="p-4 border-b theme-border-sidebar">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <h2 className="text-lg font-semibold theme-text-primary">Workflows</h2>
          )}
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded-lg hover:theme-bg-sidebar-hover theme-text-secondary hover:theme-text-primary transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 p-2">
        <div className="space-y-1">
          {workflows.map((workflow) => {
            const Icon = workflow.icon;
            const isActive = activeWorkflow === workflow.id;
            
            return (
              <button
                key={workflow.id}
                onClick={() => workflow.available && onWorkflowChange(workflow.id)}
                disabled={!workflow.available}
                className={classNames(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all duration-200',
                  isActive
                    ? 'bg-yellow-500/10 theme-accent-yellow border border-yellow-500/30'
                    : workflow.available
                    ? 'theme-text-secondary hover:theme-bg-sidebar-hover hover:theme-text-primary'
                    : 'theme-text-muted cursor-not-allowed opacity-60'
                )}
                title={isCollapsed ? workflow.name : undefined}
              >
                <Icon className={classNames(
                  'w-5 h-5 flex-shrink-0',
                  isActive ? 'theme-accent-yellow' : 'theme-text-secondary'
                )} />
                
                {!isCollapsed && (
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate theme-text-secondary">
                        {workflow.name}
                      </span>
                      {!workflow.available && (
                        <span className="text-xs theme-text-muted bg-slate-700/50 px-2 py-0.5 rounded">
                          Soon
                        </span>
                      )}
                    </div>
                    {!isCollapsed && (
                      <p className="text-xs theme-text-muted truncate mt-0.5">
                        {workflow.description}
                      </p>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Footer */}
      {!isCollapsed && (
        <div className="p-4 border-t theme-border-sidebar">
          <div className="text-xs theme-text-muted">
            <p className="font-medium mb-1">Politheon Platform</p>
            <p>More workflows coming soon</p>
          </div>
        </div>
      )}
    </div>
  );
};